// src/pages/partners/MyDetail.tsx
//
// MyDetail
// - Partner self-service "My Detail" page.
// - Displays (read-only) partner account info, API credentials, current balance, and recent transactions.
// - Primary audience: Partner users (UserGroup.PARTNER).
//   - If accessed by non-partner roles, the UI still renders but may not load data due to permission scopes.
//
// Data sources
// - Partner detail: usePartnerDetail(partnerId)
// - Transactions (paged): usePartnerTransactions({ partnerId, page, pageSize })
// - Transaction count: usePartnerTransactionCount(partnerId)
//
// UX / behavior notes
// - Page header is injected via PageHeader slots (Partner name + Partner ID).
// - Credentials can be revealed (show/hide) and copied to clipboard.
// - Transactions are rendered in a wide, horizontally-scrollable table with a dark header.
// - Recent Transactions table uses StandardTableToolbar + StandardTableFrame + StandardPagination.
//   - Loading / empty states are handled by StandardTableFrame (no local skeleton rows for the table section).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy,
  Key,
  ReceiptLong,
  Tune,
  Visibility,
  VisibilityOff,
  RemoveFromQueueRounded,
  Wallet,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { userState } from '@recoils/atoms';
import { UserGroup } from '@models/user';

import { usePartnerDetail } from '@queries/partners/usePartnerDetail';
import { usePartnerTransactions } from '@queries/partners/usePartnerTransactions';
import { usePartnerTransactionCount } from '@queries/partners/usePartnerTransactionCount';

import type { Partner, PartnerTransaction } from '@models/partner';

import { copyToClipboard } from '@utils/common';
import { timestampToDate } from '@utils/time';
import { toUsdDisplay } from '@utils/money';
import { toDisplayPartnerId } from './util';
import { getPartnerTransactionTypeMeta } from '@constants/partnerLabels';

import { usePageHeader } from '@components/common/pageHeader.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import { StandardPagination, StandardTableFrame, StandardTableToolbar } from '@components/common/table';

const PAGE_SIZE = 50;

const MONO_FONT_FAMILY =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

const EMPTY_PARTNER: Partner = {
  name: '',
  partnerId: 0,
  managerUsername: '',
  managerPassword: '',
  managerEmail: '',
  managerName: '',
  managerTelephone: '',
  address: '',
  balance: 0,
  baseDiscountRate: 0,
  callbackUrl: '',
  createdAt: '',
  appId: '',
  appSecret: '',
  remoteIps: [],
  manualOrderEnabled: false,
  status: 'ACTIVE',
};

type SectionCardProps = {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  description?: string;
  children: React.ReactNode;
};

/**
 * Generic section wrapper used on this page.
 * - Keeps layout and spacing consistent across "Account", "API", "Balance", and "Transactions".
 * - This is intentionally local to this page to avoid premature shared-component coupling.
 */
const SectionCard = ({ title, icon, right, description, children }: SectionCardProps) => (
  <Paper
    sx={(theme) => ({
      position: 'relative',
      p: 2,
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
      overflow: 'hidden',
    })}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
      mb={description ? 0.75 : 1.5}
      sx={{ minWidth: 0 }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {icon}
        <Typography variant="h6" fontWeight={700} sx={{ minWidth: 0 }}>
          {title}
        </Typography>
      </Stack>

      {right ? <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{right}</Box> : null}
    </Stack>

    {description ? (
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        {description}
      </Typography>
    ) : null}

    {children}
  </Paper>
);

type PartnerTransactionRow = {
  partnerTransactionId: number | string;
  type: string;
  partnerTransactionCode: string;
  orderCode: string;
  productCode: string;
  productName: string;
  quantity: number;
  previousBalance: number;
  addedAmount: number;
  balance?: number;
  createdAt: string | number;
};

const CODE_CELL_SX = {
  whiteSpace: 'nowrap',
  fontFamily: MONO_FONT_FAMILY,
  fontSize: 13,
} as const;

const MONEY_CELL_SX = {
  whiteSpace: 'nowrap',
  fontFamily: MONO_FONT_FAMILY,
  fontVariantNumeric: 'tabular-nums',
  fontSize: 13,
} as const;

const QTY_CELL_SX = {
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
} as const;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/**
 * Backend hardening helpers
 * - Some fields may arrive as empty strings / null / undefined.
 * - These helpers keep rendering stable and predictable without changing business semantics.
 */
const toStringOr = (v: unknown, fallback = '-') => {
  if (typeof v === 'string') {
    const t = v.trim();
    return t ? t : fallback;
  }
  return fallback;
};

const toNumberOr = (v: unknown, fallback = 0) => {
  const num = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(num) ? num : fallback;
};

const renderTypeChip = (rawType: string) => {
  const meta = getPartnerTransactionTypeMeta(rawType);

  return (
    <Chip
      size="small"
      label={meta.label}
      color={meta.chip.color}
      variant={meta.chip.variant}
      sx={{ fontWeight: 700, fontSize: 12 }}
    />
  );
};

const MyDetail = () => {
  const user = useRecoilValue(userState);
  const navigate = useNavigate();

  const { setPageHeaderSlots } = usePageHeader();
  const systemAlert = useSystemAlert();

  /**
   * Partner scope
   * - This page is primarily for partner users.
   * - When not a partner user, we keep rendering but expect queries may not return data.
   */
  const isPartnerUser = user?.group === UserGroup.PARTNER;
  const partnerId = isPartnerUser ? Number(user?.partnerId) : 0;
  const safePartnerId = Number.isFinite(partnerId) ? partnerId : 0;

  const [partner, setPartner] = useState<Partner>(EMPTY_PARTNER);

  // Paging (1-based index)
  const [page, setPage] = useState(1);

  // UI-only: reveal/copy credentials (still read-only)
  const [showPassword, setShowPassword] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  /* ------------------------------------------------------------------ */
  /* Queries                                                             */
  /* ------------------------------------------------------------------ */

  const { data: partnerDetailRes, isLoading: isPartnerLoading } = usePartnerDetail(safePartnerId);

  const { data: txRes, isLoading: isTxLoading } = usePartnerTransactions({
    partnerId: safePartnerId,
    page,
    pageSize: PAGE_SIZE,
  });

  const { data: txCount, isLoading: isTxCountLoading } = usePartnerTransactionCount(safePartnerId);

  /* ------------------------------------------------------------------ */
  /* Effects                                                             */
  /* ------------------------------------------------------------------ */

  // Load partner detail into local state for display.
  useEffect(() => {
    const loaded = partnerDetailRes?.data?.[0];
    if (!loaded) return;
    setPartner(loaded);
  }, [partnerDetailRes]);

  // Reset paging when partner changes (prevents carrying page number across accounts).
  useEffect(() => {
    setPage(1);
  }, [safePartnerId]);

  // Header: partner name + Partner ID label (monospace for scanability).
  const partnerName = useMemo(() => partner.name || 'Partner', [partner.name]);
  const partnerDisplayId = useMemo(() => toDisplayPartnerId(partner.partnerId), [partner.partnerId]);

  useEffect(() => {
    setPageHeaderSlots({
      leading: {
        title: partnerName,
        description: (
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: MONO_FONT_FAMILY }}>
            Partner ID: <b>{partnerDisplayId}</b>
          </Typography>
        ),
      },
    });
  }, [setPageHeaderSlots, partnerName, partnerDisplayId]);

  /* ------------------------------------------------------------------ */
  /* Derived state                                                        */
  /* ------------------------------------------------------------------ */

  const totalCount = txCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isTableLoading = Boolean(isTxLoading || isTxCountLoading);

  /**
   * Normalize transaction response to a UI-friendly shape.
   * - Keeps the UX stable even if backend returns null/empty fields.
   * - Does not change business meaning; only ensures safe rendering.
   */
  const transactions = useMemo<PartnerTransactionRow[]>(() => {
    const raw = (txRes?.data ?? []) as PartnerTransaction[];

    return raw.map((t, idx) => {
      const createdAt = (t as any).createdAt;
      const fallbackKey = `${createdAt ?? 'tx'}-${idx}`;

      return {
        partnerTransactionId: (t as any).partnerTransactionId ?? (t as any).partnerTransactionCode ?? fallbackKey,
        type: toStringOr((t as any).type, 'UNKNOWN'),
        partnerTransactionCode: toStringOr((t as any).partnerTransactionCode, '-'),
        orderCode: toStringOr((t as any).orderCode, '-'),
        productCode: toStringOr((t as any).productCode, '-'),
        productName: toStringOr((t as any).productName, '-'),
        quantity: toNumberOr((t as any).quantity, 0),
        previousBalance: toNumberOr((t as any).previousBalance, 0),
        addedAmount: toNumberOr((t as any).addedAmount, 0),
        balance: typeof (t as any).balance === 'number' ? (t as any).balance : undefined,
        createdAt,
      };
    });
  }, [txRes]);

  /* ------------------------------------------------------------------ */
  /* UI handlers                                                          */
  /* ------------------------------------------------------------------ */

  const handleCopy = (value: string, message: string) => {
    copyToClipboard(value ?? '');
    systemAlert.success(message);
  };

  const handleChangePage = (next: number) => {
    // Always clamp to valid page range to prevent invalid navigation states.
    setPage(clamp(next, 1, totalPages));
  };

  // (# + Transaction + TxCode + OrderCode + ProductCode + Product + Qty + PrevBal + Amount + FinalBal + Date)
  const txColumnsCount = 11;

  return (
    <Box width="100%" sx={{ minWidth: 0 }}>
      {/* Role hint (non-blocking): keep rendering but warn about potential permission constraints. */}
      {!isPartnerUser ? (
        <Paper
          variant="outlined"
          sx={(theme) => ({
            mt: 1,
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            borderColor: theme.palette.info.light,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(41, 182, 246, 0.08)' : 'rgba(2, 136, 209, 0.06)',
          })}
        >
          <Typography variant="body2" color="text.secondary">
            This page is intended for Partner accounts. Some sections may not load if you are not signed in as a Partner.
          </Typography>
        </Paper>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* Account Information (read-only)                                     */}
      {/* ------------------------------------------------------------------ */}
      <Box mb={2}>
        <SectionCard
          title="Account Information"
          icon={<Tune fontSize="small" />}
          description="Portal account details (read-only)."
        >
          {isPartnerLoading ? (
            <Stack spacing={1.5}>
              <Skeleton height={48} />
              <Skeleton height={48} />
              <Skeleton height={48} />
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              <TextField
                label="Admin ID"
                value={partner.managerUsername ?? ''}
                size="small"
                fullWidth
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleCopy(partner.managerUsername ?? '', 'Admin ID copied')}
                          aria-label="Copy admin id"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Admin Password"
                value={partner.managerPassword ?? ''}
                size="small"
                fullWidth
                disabled
                type={showPassword ? 'text' : 'password'}
                helperText={
                  <Typography variant="caption" color="text.secondary">
                    Credentials are managed by admins. Contact support if changes are needed.
                  </Typography>
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? 'Hide' : 'Show'}>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Copy">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleCopy(partner.managerPassword ?? '', 'Password copied')}
                          aria-label="Copy password"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
                <TextField label="Account Manager" value={partner.managerName ?? ''} size="small" fullWidth disabled />
                <TextField label="Email" value={partner.managerEmail ?? ''} size="small" fullWidth disabled />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
                <TextField label="Phone" value={partner.managerTelephone ?? ''} size="small" fullWidth disabled />
                <TextField label="Address" value={partner.address ?? ''} size="small" fullWidth disabled />
              </Stack>
            </Stack>
          )}
        </SectionCard>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* API Settings (read-only)                                            */}
      {/* ------------------------------------------------------------------ */}
      <Box mb={2}>
        <SectionCard title="API Settings" icon={<Key fontSize="small" />} description="API credentials are read-only for this role.">
          {isPartnerLoading ? (
            <Stack spacing={1.5}>
              <Skeleton height={48} />
              <Skeleton height={48} />
              <Skeleton height={48} />
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              <TextField
                label="App ID"
                value={partner.appId ?? ''}
                size="small"
                fullWidth
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleCopy(partner.appId ?? '', 'App ID copied')}
                          aria-label="Copy app id"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="App Secret"
                value={partner.appSecret ?? ''}
                size="small"
                fullWidth
                disabled
                type={showSecret ? 'text' : 'password'}
                helperText={
                  <Typography variant="caption" color="text.secondary">
                    Keep this secret secure. Rotate via admin if required.
                  </Typography>
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showSecret ? 'Hide' : 'Show'}>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setShowSecret((v) => !v)}
                          aria-label={showSecret ? 'Hide app secret' : 'Show app secret'}
                        >
                          {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Copy">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleCopy(partner.appSecret ?? '', 'App Secret copied')}
                          aria-label="Copy app secret"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Callback URL"
                value={partner.callbackUrl ?? ''}
                size="small"
                fullWidth
                disabled
                placeholder="https://..."
              />
            </Stack>
          )}
        </SectionCard>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Balance (read-only)                                                 */}
      {/* ------------------------------------------------------------------ */}
      <Box mb={2}>
        <SectionCard title="Balance" icon={<Wallet />} description="Current balance (read-only).">
          {isPartnerLoading ? (
            <Skeleton height={48} />
          ) : (
            <TextField label="Current Balance" value={toUsdDisplay(partner.balance)} size="small" fullWidth disabled />
          )}
        </SectionCard>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Shortcut to pricing page                                            */}
      {/* ------------------------------------------------------------------ */}
      <Box mb={2}>
        <SectionCard
          title="Product Authorization and Pricing"
          icon={<RemoveFromQueueRounded fontSize="small" />}
          description="View product visibility and pricing rules."
          right={
            <Button
              variant="contained"
              onClick={() => navigate('/my-detail/prices')}
              sx={{ whiteSpace: 'nowrap', px: 2.5, fontWeight: 700 }}
            >
              Open Pricing
            </Button>
          }
        >
          <Typography variant="body2" color="text.secondary">
            Pricing configuration is managed on a separate page.
          </Typography>
        </SectionCard>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Transactions                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" mb={1} sx={{ minWidth: 0 }}>
          <ReceiptLong fontSize="small" />
          <Typography variant="h6" fontWeight={800}>
            Recent Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            (KST)
          </Typography>
        </Stack>

        {/* Standard toolbar (top) */}
        <StandardTableToolbar
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount}
          onChangePage={handleChangePage}
          showTotalCount
          showPageJump
        />

        {/* Standard table */}
        <Box mt={1}>
          <StandardTableFrame<PartnerTransactionRow>
            containerSx={{
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              overflowX: 'auto',
            }}
            tableSx={{ minWidth: 1180 }}
            columnsCount={txColumnsCount}
            isLoading={isTableLoading}
            loadingVariant="skeleton"
            skeletonRows={6}
            emptyText="No transactions found."
            head={
              <TableRow>
                <TableCell width={70} align="right">
                  #
                </TableCell>
                <TableCell align="center">Transaction</TableCell>
                <TableCell>Transaction Code</TableCell>
                <TableCell>Order Code</TableCell>
                <TableCell>Product Code</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Prev Balance</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Final Balance</TableCell>
                <TableCell align="center">Date (KST)</TableCell>
              </TableRow>
            }
            headSx={(theme) => ({
              '& .MuiTableCell-head': {
                backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[900] : theme.palette.grey[800],
                color: theme.palette.common.white,
                fontWeight: 700,
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                whiteSpace: 'nowrap',
              },
            })}
            rows={transactions}
            getRowKey={(row) => String(row.partnerTransactionId)}
            renderRow={(t, index) => {
              const amount = Number(t.addedAmount ?? 0);
              const finalBalance =
                typeof t.balance === 'number' ? t.balance : Number(t.previousBalance ?? 0) + amount;

              return (
                <>
                  <TableCell align="right" sx={QTY_CELL_SX}>
                    {(page - 1) * PAGE_SIZE + index + 1}
                  </TableCell>

                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    {renderTypeChip(t.type)}
                  </TableCell>

                  <TableCell sx={CODE_CELL_SX}>{t.partnerTransactionCode}</TableCell>
                  <TableCell sx={CODE_CELL_SX}>{t.orderCode}</TableCell>
                  <TableCell sx={CODE_CELL_SX}>{t.productCode}</TableCell>

                  <TableCell sx={{ minWidth: 240 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {t.productName}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={QTY_CELL_SX}>
                    {t.quantity.toLocaleString()}
                  </TableCell>

                  <TableCell align="right" sx={MONEY_CELL_SX}>
                    {toUsdDisplay(t.previousBalance)}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={(theme) => ({
                      ...MONEY_CELL_SX,
                      fontWeight: 900,
                      color:
                        amount > 0
                          ? theme.palette.success.main
                          : amount < 0
                            ? theme.palette.error.main
                            : theme.palette.text.primary,
                    })}
                  >
                    {toUsdDisplay(amount)}
                  </TableCell>

                  <TableCell align="right" sx={MONEY_CELL_SX}>
                    {toUsdDisplay(finalBalance)}
                  </TableCell>

                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    {timestampToDate(t.createdAt)}
                  </TableCell>
                </>
              );
            }}
          />
        </Box>

        {/* Standard pagination (bottom) */}
        <Box mt={2} display="flex" justifyContent="center">
          <StandardPagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onChangePage={handleChangePage}
            size="small"
            shape="rounded"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MyDetail;
